import { atom, createStore, useAtom, type WritableAtom } from "jotai";
import { atomWithStorage, createJSONStorage, RESET } from "jotai/utils";
import { atomWithHash, atomWithLocation } from "jotai-location";

export const AppStore = createStore();

export const locationAtom = atomWithLocation({ replace: false });
export const locationAtomWithReplace = atomWithLocation({ replace: true });

export const atomWithStorageRedundancy = <T>(
  key: string,
  initialFilterAtomValue: T
) => {
  const sessionFilterAtom = atomWithStorage(
    key,
    initialFilterAtomValue,
    createJSONStorage(() => window.sessionStorage)
  );

  const storageFilterAtom = atomWithStorage(key, initialFilterAtomValue);

  return atom(
    (get) => {
      const sessionState = get(sessionFilterAtom);
      const storageState = get(storageFilterAtom);
      if (sessionState !== initialFilterAtomValue) {
        return sessionState;
      }
      return storageState;
    },
    (_get, set, value: T | typeof RESET) => {
      if (value === RESET) {
        void set(sessionFilterAtom, initialFilterAtomValue);
        void set(storageFilterAtom, initialFilterAtomValue);
      } else {
        void set(sessionFilterAtom, value);
        void set(storageFilterAtom, value);
      }
    }
  );
};

/**
 * Generates serialize/deserialize functions for arrays to be used in URL query parameters.
 * Uses double encoding (JSON.stringify + encodeURIComponent) for safe transport in query strings.
 * Output example: %255B%2522464684%2522%255D (URL-safe but verbose)
 */
export const generateArraySerializeFns = <
  T extends unknown[],
  Y extends "string" | "number",
>() => {
  type returnType = Y extends "number" ? number[] : string[];
  return {
    serialize(a: T) {
      try {
        return encodeURIComponent(JSON.stringify(a));
      } catch {
        return "";
      }
    },
    deserialize: (value: string): returnType => {
      try {
        const parsed = JSON.parse(decodeURIComponent(value));
        return (Array.isArray(parsed) ? parsed : []) as unknown as returnType;
      } catch {
        return [] as unknown as returnType;
      }
    },
  };
};

/**
 * Generates serialize/deserialize functions for arrays to be used in URL hash fragments.
 * Uses tilde-separated values for clean, compact hash values without URL encoding.
 * Output example: 464684~789012 (minimal encoding, human-readable, URL-safe)
 * Note: Only works with arrays of primitives (strings/numbers). Tilde is unreserved per RFC 3986 and rarely used in search terms.
 */
export const generateArraySerializeFns2 = <T extends unknown[]>() => ({
  serialize(a: T): string {
    return a.join("~");
  },
  deserialize(value: string): T {
    return (value ? value.split("~") : []) as unknown as T;
  },
});

export const generateHashAtomHook = <T>(
  key: string,
  initialValue: T,
  options: {
    serialize?: (val: T) => string;
    deserialize?: (str: string) => T;
  } = {}
) => {
  const fullOptions = {
    setHash: "replaceState" as const,
    ...options,
  };
  const atom = atomWithHash<T>(key, initialValue, fullOptions);
  atom.debugLabel = key;
  const hook = () => useAtom(atom);

  hook.atom = atom;

  return hook;
};

export const generateAtomHook = <T>(initialValue: T) => {
  const newAtom = atom<T>(initialValue);
  const hook = () => useAtom(newAtom);

  hook.atom = newAtom;

  return hook;
};

interface UrlQueryAtomOptions<T extends string | number | boolean> {
  updateUrl?: boolean;
  replaceUrl?: boolean;
  defaultValue?: T;
}

// DEPRECATED - DO NOT USE. Use `useSearchParamState` instead.
// jotai location does not sync well with react-router and the below
// code is needlessly complicated and has no benefits over useSearchParamState.
export const createUrlQueryAtom = <T extends string | number | boolean>(
  paramName: string,
  options?: UrlQueryAtomOptions<T>
) => {
  const { updateUrl = true, defaultValue, replaceUrl = false } = options || {};
  const _locationAtom = replaceUrl ? locationAtomWithReplace : locationAtom;

  if (updateUrl && paramName) {
    let prevValue: T | undefined = defaultValue;

    const searchQueryAtom = atom(
      // if the value is undefined, return the previous value
      (get) => get(_locationAtom).searchParams?.get(paramName) || prevValue,
      (get, set, value: T | undefined) => {
        const location = get(_locationAtom);
        const searchParams = new URLSearchParams(location.searchParams);

        prevValue = value;

        if (value) {
          searchParams.set(paramName, String(value));
        } else {
          searchParams.delete(paramName);
        }

        set(_locationAtom, () => ({ searchParams }));
      }
    );

    searchQueryAtom.debugLabel = `searchQueryAtom (${paramName})`;
    return searchQueryAtom;
  } else {
    // If updateUrl is false, create a simple atom with the default value
    // This atom won't sync with the URL
    return atom(defaultValue as T | undefined);
  }
};

/**
 * https://jotai.org/docs/recipes/atom-with-toggle
 * Creates an atom that can be used to toggle a boolean value.
 * @param initialValue - The initial value of the atom.
 * @returns An atom that can be used to toggle a boolean value.
 */
export const atomWithToggle = (
  initialValue?: boolean
): WritableAtom<boolean, [unknown?], void> => {
  const anAtom = atom(!!initialValue, (get, set, nextValue?: unknown) => {
    // Only use nextValue directly if it's a boolean, otherwise toggle
    const update = typeof nextValue === "boolean" ? nextValue : !get(anAtom);
    set(anAtom, update);
  });

  return anAtom as WritableAtom<boolean, [unknown?], void>;
};
